import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight, Phone, Mail } from "lucide-react";
import { PRODUCTS } from "../data/products";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/products", label: "Products", hasMega: true },
  { to: "/industries", label: "Industries" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" }
];

const LOGO_URL = "https://customer-assets.emergentagent.com/job_weld-overlay-tech/artifacts/y6ehzf11_Weldmatix%20Fusion%20Logo.png";

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [mega, setMega] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setMega(false);
  }, [loc.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-white ${
        scrolled ? "border-b border-[#E2E8F0] shadow-[0_1px_0_0_rgba(15,23,42,0.04)]" : "border-b border-transparent"
      }`}
    >
      {/* Top utility strip — clean OEM style */}
      <div className="hidden md:block border-b border-[#F1F5F9] bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 flex items-center justify-between text-[11px] font-mono-wm uppercase tracking-[0.2em] text-[#64748B] py-2">
          <span data-testid="header-tagline">Welding Automation · Weld Overlay · Cladding Systems</span>
          <div className="flex items-center gap-6">
            <a data-testid="header-sales-email" href="mailto:sales@clangrp.com" className="flex items-center gap-2 hover:text-[#0EA5E9]">
              <Mail className="w-3 h-3" /> sales@clangrp.com
            </a>
            <span className="text-[#CBD5E1]">|</span>
            <span className="text-[#0EA5E9]">ISO 9001 · Global OEM</span>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-12 flex items-center justify-between py-4">
        {/* Logo upper-left — Fronius / ABB style */}
        <Link to="/" data-testid="header-logo" className="flex items-center gap-3 shrink-0">
          <img
            src={LOGO_URL}
            alt="Weldmatix Fusion"
            className="logo-img h-12 md:h-14 w-auto object-contain"
          />
          <div className="hidden sm:block leading-tight border-l border-[#E2E8F0] pl-3 ml-1">
            <div className="font-display font-bold text-[#0F172A] text-base tracking-tight">WELDMATIX FUSION</div>
            <div className="font-mono-wm text-[10px] tracking-[0.3em] text-[#64748B] uppercase">Welding Automation OEM</div>
          </div>
        </Link>

        {/* Main nav */}
        <nav className="hidden lg:flex items-center gap-1">
          {NAV.map((n) => (
            <div
              key={n.to}
              onMouseEnter={() => n.hasMega && setMega(true)}
              onMouseLeave={() => n.hasMega && setMega(false)}
              className="relative"
            >
              <NavLink
                to={n.to}
                end={n.to === "/"}
                data-testid={`nav-${n.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `px-4 py-2 text-[13px] uppercase tracking-[0.15em] font-medium hover-line ${
                    isActive ? "text-[#0EA5E9]" : "text-[#334155] hover:text-[#0F172A]"
                  }`
                }
              >
                {n.label}
              </NavLink>

              {n.hasMega && mega && (
                <div className="absolute left-1/2 -translate-x-1/2 top-full pt-3 w-[720px]" data-testid="mega-products">
                  <div className="bg-white border border-[#E2E8F0] shadow-[0_24px_48px_-16px_rgba(15,23,42,0.18)] grid grid-cols-2 gap-0">
                    {PRODUCTS.slice(0, 8).map((p) => (
                      <Link
                        key={p.slug}
                        to={`/products/${p.slug}`}
                        data-testid={`mega-product-${p.slug}`}
                        className="group flex items-start gap-3 p-4 border-b border-r border-[#F1F5F9] hover:bg-[#F8FAFC] transition"
                      >
                        <div className="w-14 h-14 flex-shrink-0 overflow-hidden border border-[#E2E8F0] bg-white">
                          <img src={p.image} alt={p.name} className="w-full h-full object-cover" />
                        </div>
                        <div>
                          <div className="font-display text-sm text-[#0F172A] group-hover:text-[#0EA5E9]">{p.name}</div>
                          <div className="font-mono-wm text-[10px] tracking-[0.2em] text-[#64748B] mt-1 uppercase">{p.short}</div>
                        </div>
                      </Link>
                    ))}
                    <Link
                      to="/products"
                      data-testid="mega-view-all"
                      className="col-span-2 flex items-center justify-between p-4 bg-[#0F172A] hover:bg-[#0EA5E9] text-white transition"
                    >
                      <span className="font-mono-wm text-xs uppercase tracking-[0.2em]">View all systems</span>
                      <ArrowUpRight className="w-4 h-4" />
                    </Link>
                  </div>
                </div>
              )}
            </div>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <Link
            to="/contact"
            data-testid="header-cta-quote"
            className="group flex items-center gap-2 bg-[#0EA5E9] hover:bg-[#0284C7] text-white px-5 py-2.5 text-[13px] font-medium uppercase tracking-[0.15em] transition-colors"
          >
            Request Consultation
            <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </Link>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((s) => !s)}
          className="lg:hidden text-[#0F172A] p-2"
          aria-label="Toggle menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-white border-t border-[#E2E8F0]" data-testid="mobile-menu">
          <div className="px-6 py-4 flex flex-col gap-1">
            {NAV.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                data-testid={`mobile-nav-${n.label.toLowerCase()}`}
                className="py-3 text-sm uppercase tracking-[0.2em] text-[#334155] hover:text-[#0EA5E9] border-b border-[#F1F5F9]"
              >
                {n.label}
              </Link>
            ))}
            <Link
              to="/contact"
              data-testid="mobile-cta-quote"
              className="mt-4 bg-[#0EA5E9] hover:bg-[#0284C7] text-white px-5 py-3 text-sm uppercase tracking-[0.2em] text-center"
            >
              Request Consultation
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
